<template>
  <svg class="histogram" v-bind:viewBox="`0 0 ${width} ${height}`" :width="width" :height="height">
    <path class="data" fill="white" v-bind:d="path" />

    <g class="x-axis">
      <path :d="`M ${marginLeft} ${canvasHeight} ${marginLeft + canvasWidth} ${canvasHeight}`" />
      <path :d="`M ${marginLeft} ${canvasHeight - 5} ${marginLeft} ${canvasHeight + 5}`" />
      <path
        :d="`M ${marginLeft + canvasWidth} ${canvasHeight - 5} ${marginLeft + canvasWidth} ${
          canvasHeight + 5
        }`"
      />
      <path :d="`M ${scaleX(0)} ${canvasHeight} ${scaleX(0)} ${canvasHeight + 8}`" />
    </g>

    <text class="prognosed-time" :x="scaleX(0)" :y="canvasHeight + 22">
      {{ timeFormatted }}
    </text>
    <text
      v-if="plannedTimeFormatted && timeFormatted !== plannedTimeFormatted"
      class="planned-time"
      :x="scaleX(0) + 45"
      :y="canvasHeight + 22"
    >
      {{ plannedTimeFormatted }}
    </text>
    <text class="max-delay" :x="marginLeft + canvasWidth + 2" :y="canvasHeight">
      +{{ maxDelayShown }}
    </text>
    <text class="min-delay" :x="marginLeft - 2" :y="canvasHeight">-{{ minDelayShown }}</text>
  </svg>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Dayjs } from 'dayjs'
import { type DelayPrediction } from '~/types/fptfTypes'

const props = defineProps<{
  time?: Dayjs
  plannedTime?: Dayjs
  prediction: DelayPrediction
  cancelled?: boolean
}>()

const width = 85
const height = 60
const marginLeft = 13
const marginRight = 25
const marginBottom = 25
const canvasHeight = height - marginBottom
const canvasWidth = width - marginLeft - marginRight
const maxDelayShown = 10
const minDelayShown = -2

const maxDelayIndex = computed<number>(() => {
  return Math.min(props.prediction.predictions.length, maxDelayShown + props.prediction.offset)
})
const minDelayIndex = computed<number>(() => {
  return Math.max(0, props.prediction.offset + minDelayShown)
})
const nPredictions = computed<number>(() => {
  return maxDelayIndex.value - minDelayIndex.value
})

function scaleX(value: number) {
  value = value - minDelayShown
  value = (value * canvasWidth) / nPredictions.value + marginLeft
  value = Math.max(marginLeft, value)
  value = Math.min(marginLeft + canvasWidth, value)
  return value
}

function scaleY(value: number) {
  return (value * canvasHeight) / 0.5
}

const timeFormatted = computed(() => {
  return props.time?.format('HH:mm') || props.plannedTime?.format('HH:mm')
})
const plannedTimeFormatted = computed(() => {
  return props.plannedTime?.format('HH:mm')
})

const path = computed(() => {
  if (props.cancelled === true) {
    return ''
  }
  let pathString = `M ${marginLeft} ${canvasHeight} `
  for (let i = minDelayIndex.value - 1; i < maxDelayIndex.value; i++) {
    pathString += `${scaleX(i - 0.5 - props.prediction.offset)} ${
      canvasHeight - scaleY(props.prediction.predictions[i])
    } `
    pathString += `${scaleX(i + 0.5 - props.prediction.offset)} ${
      canvasHeight - scaleY(props.prediction.predictions[i])
    } `
  }
  pathString += `${marginLeft + canvasWidth} ${canvasHeight} ${marginLeft} ${canvasHeight}`
  return pathString
})
</script>

<style lang="scss">
svg.histogram {
  .data {
    fill: $body-color-dark;
  }

  .x-axis {
    stroke: $body-color-dark;
    stroke-width: 2;
  }

  text {
    fill: $body-color-dark;
    font-size: var(--bs-body-font-size);

    &.max-delay {
      text-anchor: start;
      font-size: 0.8rem;
      dominant-baseline: central;
    }

    &.min-delay {
      text-anchor: end;
      font-size: 0.8rem;
      dominant-baseline: central;
    }

    &.prognosed-time {
      text-anchor: middle;
      font-weight: bold;
    }

    &.planned-time {
      text-anchor: middle;
      text-decoration: line-through;
      fill: var(--bs-secondary-color);
    }
  }
}
</style>
